<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import { ManageOrganisationService } from '@/services/manageOrganisation.services';
import { ManageOrganisationMutation } from '@/mutations/manageOrganisation.mutations';
import axiosInstance from '@/services/interceptor.services';
import type {
  IManageOrganisation,
  IOrganisationStore,
} from '@/types/manageOrgCommerce.types';
import type { IRestaurant } from '@/types/commerce.types';

// ── Types ──────────────────────────────────────────────────────────────────
type ViewMode = 'table' | 'card';

interface OrgRow extends IManageOrganisation {
  _stores: IOrganisationStore[];
}

const { t } = useI18n({ useScope: 'global' });

// ── Filtre statut + tri API ────────────────────────────────────────────────
type StatusFilter = 'active' | 'disabled' | 'all';
type ApiSortKey = 'name' | 'createdAt' | 'storeCount' | 'activeStatus';
type SortDir = 'ASC' | 'DESC';

const statusFilter = ref<StatusFilter>('active');
const STATUS_OPTS = computed<{ k: StatusFilter; l: string }[]>(() => [
  { k: 'active', l: t('clients.status.active') },
  { k: 'disabled', l: t('clients.status.disabled') },
  { k: 'all', l: t('clients.status.all') },
]);
function apiStatus(f: StatusFilter): string[] {
  if (f === 'active') return ['active'];
  if (f === 'disabled') return ['disabled'];
  return [];
}

const sortKey = ref<ApiSortKey>('name');
const sortDir = ref<SortDir>('ASC');
function setSort(key: ApiSortKey) {
  if (sortKey.value === key)
    sortDir.value = sortDir.value === 'ASC' ? 'DESC' : 'ASC';
  else {
    sortKey.value = key;
    sortDir.value = 'ASC';
  }
}

// ── Chargement organisations (toutes pages) ────────────────────────────────
const {
  data: rawData,
  isPending,
  isError,
} = useQuery({
  queryKey: computed(() => [
    'clients-page-orgs',
    statusFilter.value,
    sortKey.value,
    sortDir.value,
  ]),
  queryFn: async () => {
    const status = apiStatus(statusFilter.value);
    const sb = sortKey.value;
    const so = sortDir.value;
    const first = await ManageOrganisationService.searchOrganisations(
      1,
      status,
      sb,
      so,
    );
    const meta = first.data as any;
    const total: number = meta?.total ?? 0;
    const pageSize: number = meta?.pageSize ?? 20;
    const extract = (r: any): IManageOrganisation[] => {
      const d = r?.data;
      return Array.isArray(d) ? d : (d?.data ?? []);
    };
    let orgs = extract(first);
    if (total > pageSize) {
      const pages = Array.from(
        { length: Math.ceil(total / pageSize) - 1 },
        (_, i) => i + 2,
      );
      const rest = await Promise.all(
        pages.map((p) =>
          ManageOrganisationService.searchOrganisations(p, status, sb, so).then(
            extract,
          ),
        ),
      );
      orgs = [...orgs, ...rest.flat()];
    }
    const activeOrgs = orgs.filter((o) => o.isActive);
    const storeResults = await Promise.all(
      activeOrgs.map((o) =>
        axiosInstance
          .get<{ data: IRestaurant[] }>(
            `restaurants/search?pagination=true&page=1&pageSize=50&organizationId=${o.id}`,
          )
          .then((r) => ({ orgId: o.id, stores: r.data.data ?? [] }))
          .catch(() => ({ orgId: o.id, stores: [] as IRestaurant[] })),
      ),
    );
    const storeMap = new Map(storeResults.map((r) => [r.orgId, r.stores]));
    return orgs.map((o) => ({
      ...o,
      _stores: (storeMap.get(o.id) ?? []).map((s) => ({
        id: s.id,
        designation: s.designation,
        slug: s.slugs?.[0]?.slug ?? null,
        isActive: s.isActive ?? true,
      })) as IOrganisationStore[],
    })) as OrgRow[];
  },
  staleTime: 60_000,
});

const allOrgs = computed<OrgRow[]>(() => rawData.value ?? []);

// ── Vue ────────────────────────────────────────────────────────────────────
const viewMode = ref<ViewMode>('card');

// ── Sélection ──────────────────────────────────────────────────────────────
const selected = ref<Set<string>>(new Set());
const allSelected = computed(
  () =>
    filteredOrgs.value.length > 0 &&
    filteredOrgs.value.every((o) => selected.value.has(o.id)),
);
const someSelected = computed(() => selected.value.size > 0);

function toggleAll() {
  if (allSelected.value) selected.value = new Set();
  else selected.value = new Set(filteredOrgs.value.map((o) => o.id));
}
function toggleRow(id: string) {
  const s = new Set(selected.value);
  s.has(id) ? s.delete(id) : s.add(id);
  selected.value = s;
}

// ── Recherche globale ──────────────────────────────────────────────────────
const search = ref('');

// ── Filtres par colonne ────────────────────────────────────────────────────
const colFilters = ref<Record<string, string>>({
  name: '',
  type: '',
  isActive: '',
  storeCount: '',
  taxIdentificationNumber: '',
  countryName: '',
});
const filterOpenCol = ref<string | null>(null);
function toggleColFilter(col: string) {
  filterOpenCol.value = filterOpenCol.value === col ? null : col;
}

// ── Redimensionnement colonnes ─────────────────────────────────────────────
const colWidths = ref<Record<string, number>>({
  logo: 56,
  name: 220,
  type: 120,
  tax: 160,
  stores: 90,
  status: 130,
  country: 130,
  actions: 56,
});
let resizing: { col: string; startX: number; startW: number } | null = null;
function startResize(col: string, e: MouseEvent) {
  resizing = { col, startX: e.clientX, startW: colWidths.value[col] };
  document.addEventListener('mousemove', onResize);
  document.addEventListener('mouseup', stopResize);
}
function onResize(e: MouseEvent) {
  if (!resizing) return;
  colWidths.value[resizing.col] = Math.max(
    60,
    resizing.startW + e.clientX - resizing.startX,
  );
}
function stopResize() {
  resizing = null;
  document.removeEventListener('mousemove', onResize);
  document.removeEventListener('mouseup', stopResize);
}

// ── Données filtrées + triées ──────────────────────────────────────────────
const filteredOrgs = computed(() => {
  let list = allOrgs.value;

  // Recherche globale
  const q = search.value.trim().toLowerCase();
  if (q)
    list = list.filter(
      (o) =>
        o.name.toLowerCase().includes(q) ||
        (o.taxIdentificationNumber ?? '').toLowerCase().includes(q) ||
        (o.type ?? '').toLowerCase().includes(q),
    );

  // Filtres par colonne
  for (const [col, val] of Object.entries(colFilters.value)) {
    if (!val) continue;
    const v = val.toLowerCase();
    if (col === 'name')
      list = list.filter((o) => o.name.toLowerCase().includes(v));
    if (col === 'type')
      list = list.filter((o) => (o.type ?? '').toLowerCase().includes(v));
    if (col === 'taxIdentificationNumber')
      list = list.filter((o) =>
        (o.taxIdentificationNumber ?? '').toLowerCase().includes(v),
      );
    if (col === 'isActive') {
      const active = v === 'actif' || v === 'true' || v === 'oui';
      const inactive = v === 'inactif' || v === 'false' || v === 'non';
      if (active) list = list.filter((o) => o.isActive);
      if (inactive) list = list.filter((o) => !o.isActive);
    }
    if (col === 'storeCount') {
      const n = parseInt(v);
      if (!isNaN(n)) list = list.filter((o) => (o.storeCount ?? 0) === n);
    }
    if (col === 'countryName')
      list = list.filter((o) =>
        ((o as any).countryName ?? (o as any).countryCode ?? '')
          .toLowerCase()
          .includes(v),
      );
  }

  // Tri local (miroir du tri API, appliqué sur le sous-ensemble filtré)
  list = [...list].sort((a, b) => {
    let va: any, vb: any;
    if (sortKey.value === 'name') {
      va = a.name.toLowerCase();
      vb = b.name.toLowerCase();
    }
    if (sortKey.value === 'storeCount') {
      va = a.storeCount ?? 0;
      vb = b.storeCount ?? 0;
    }
    if (sortKey.value === 'activeStatus') {
      va = a.isActive ? 1 : 0;
      vb = b.isActive ? 1 : 0;
    }
    if (sortKey.value === 'createdAt') {
      va = a.createdAt ?? '';
      vb = b.createdAt ?? '';
    }
    if (va === undefined) return 0;
    if (va < vb) return sortDir.value === 'ASC' ? -1 : 1;
    if (va > vb) return sortDir.value === 'ASC' ? 1 : -1;
    return 0;
  });

  return list;
});

// ── Pagination ─────────────────────────────────────────────────────────────
const PAGE_SIZE = 25;
const page = ref(1);
watch(filteredOrgs, () => {
  page.value = 1;
});
const totalPages = computed(() =>
  Math.ceil(filteredOrgs.value.length / PAGE_SIZE),
);
const pagedOrgs = computed(() =>
  filteredOrgs.value.slice(
    (page.value - 1) * PAGE_SIZE,
    page.value * PAGE_SIZE,
  ),
);
const pages = computed(() => {
  const p = page.value,
    total = totalPages.value;
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const arr: (number | '…')[] = [1];
  if (p > 3) arr.push('…');
  for (let i = Math.max(2, p - 1); i <= Math.min(total - 1, p + 1); i++)
    arr.push(i);
  if (p < total - 2) arr.push('…');
  arr.push(total);
  return arr;
});

// ── Désactivation inline ───────────────────────────────────────────────────
const queryClient = useQueryClient();
const {
  handleDeactivateOrganisation,
  deactivateOrganisation,
  handleUpdateOrganisation,
} = ManageOrganisationMutation();

const deactivateTarget = ref<OrgRow | null>(null);
const deactivateError = ref<string | null>(null);

function askDeactivate(org: OrgRow, e: Event) {
  e.stopPropagation();
  deactivateError.value = null;
  deactivateTarget.value = org;
}

async function activateOrg(org: OrgRow, e: Event) {
  e.stopPropagation();
  try {
    await handleUpdateOrganisation(org.id, { isActive: true });
    await queryClient.invalidateQueries({ queryKey: ['clients-page-orgs'] });
    await queryClient.invalidateQueries({ queryKey: ['GetOrganisations'] });
  } catch (err: any) {
    const raw = err?.response?.data?.message;
    console.error(Array.isArray(raw) ? raw[0] : (raw ?? err?.message));
  }
}

async function confirmDeactivate() {
  if (!deactivateTarget.value) return;
  deactivateError.value = null;
  try {
    await handleDeactivateOrganisation(deactivateTarget.value.id);
    await queryClient.invalidateQueries({ queryKey: ['clients-page-orgs'] });
    await queryClient.invalidateQueries({ queryKey: ['GetOrganisations'] });
    deactivateTarget.value = null;
  } catch (err: any) {
    const raw = err?.response?.data?.message;
    deactivateError.value = Array.isArray(raw)
      ? raw[0]
      : (raw ?? err?.message ?? 'Erreur lors de la désactivation');
  }
}

// ── Actions groupées ───────────────────────────────────────────────────────
const bulkMenuOpen = ref(false);

function bulkDeactivate() {
  bulkMenuOpen.value = false;
  // TODO: appel API désactivation groupée
}
function bulkExport() {
  bulkMenuOpen.value = false;
  exportCSV([...allOrgs.value.filter((o) => selected.value.has(o.id))]);
}
function bulkDelete() {
  bulkMenuOpen.value = false;
  // TODO: appel API suppression
}

// ── Export CSV ─────────────────────────────────────────────────────────────
function exportCSV(list: OrgRow[] = filteredOrgs.value) {
  const rows = [
    [
      t('clients.csv.name'),
      t('clients.csv.type'),
      t('clients.csv.tax'),
      t('clients.csv.stores'),
      t('clients.csv.status'),
      t('clients.csv.country'),
    ],
    ...list.map((o) => [
      o.name,
      o.type ?? '',
      o.taxIdentificationNumber ?? '',
      String(o.storeCount ?? 0),
      o.isActive
        ? t('clients.csvStatusActive')
        : t('clients.csvStatusInactive'),
      (o as any).countryName ?? (o as any).countryCode ?? '',
    ]),
  ];
  const csv = rows
    .map((r) => r.map((v) => `"${v.replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'clients.csv';
  a.click();
  URL.revokeObjectURL(url);
}

// ── Helpers ────────────────────────────────────────────────────────────────
const TYPE_LABELS = computed<Record<string, string>>(() => ({
  INDIVIDUAL: t('clients.type.INDIVIDUAL'),
  COMPANY: t('clients.type.COMPANY'),
  ASSOCIATION: t('clients.type.ASSOCIATION'),
  PUBLIC_ENTITY: t('clients.type.PUBLIC_ENTITY'),
}));
function typeLabel(type: string | null) {
  return type ? (TYPE_LABELS.value[type] ?? type) : '—';
}
function initials(name: string) {
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}
function fmtDate(d: string) {
  return new Date(d).toLocaleDateString('fr-BE', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

const router = useRouter();
function goToDetail(id: string) {
  router.push(`/clients/${id}`);
}

const AVATAR_COLORS = [
  ['#fff7ee', '#f68402'],
  ['#eef3ff', '#3b5bdb'],
  ['#f0fff4', '#2f9e44'],
  ['#fff0f6', '#c2255c'],
  ['#f8f0fe', '#7048e8'],
  ['#e6fcf5', '#0c8599'],
  ['#fff9db', '#e67700'],
  ['#fff5f5', '#c92a2a'],
];
function avatarStyle(name: string) {
  const i = name.charCodeAt(0) % AVATAR_COLORS.length;
  return { background: AVATAR_COLORS[i][0], color: AVATAR_COLORS[i][1] };
}
</script>

<template>
  <div
    class="cp"
    @click="
      filterOpenCol = null;
      bulkMenuOpen = false;
    "
  >
    <!-- ── En-tête ──────────────────────────────────────────────────────── -->
    <div class="cp__header">
      <div class="cp__header-left">
        <h1 class="cp__title">{{ t('clients.title') }}</h1>
        <p class="cp__sub">{{ t('clients.sub') }}</p>
      </div>
      <div class="cp__header-right">
        <button class="cp__btn cp__btn--ghost" @click="exportCSV()">
          <i class="pi pi-download" /> {{ t('clients.export') }}
        </button>
        <button
          class="cp__btn cp__btn--primary"
          @click="router.push('/clients/new')"
        >
          <i class="pi pi-plus" /> {{ t('clients.newClient') }}
        </button>
      </div>
    </div>

    <!-- ── Barre d'outils ───────────────────────────────────────────────── -->
    <div class="cp__toolbar">
      <!-- Filtre statut -->
      <div class="cp__status-tabs">
        <button
          v-for="opt in STATUS_OPTS"
          :key="opt.k"
          class="cp__status-tab"
          :class="{ 'cp__status-tab--active': statusFilter === opt.k }"
          @click.stop="
            statusFilter = opt.k;
            page = 1;
          "
        >
          {{ opt.l }}
        </button>
      </div>

      <!-- Recherche -->
      <div class="cp__search-wrap">
        <i class="pi pi-search cp__search-icon" />
        <input
          v-model="search"
          class="cp__search"
          type="text"
          :placeholder="t('clients.search')"
          @click.stop
        />
        <button v-if="search" class="cp__search-clear" @click="search = ''">
          <i class="pi pi-times" />
        </button>
      </div>

      <div class="cp__toolbar-right">
        <!-- Compteur résultats -->
        <span class="cp__count">
          {{ filteredOrgs.length }} / {{ allOrgs.length }}
          {{ t('clients.title').toLowerCase() }}
        </span>

        <!-- Actions groupées -->
        <div v-if="someSelected" class="cp__bulk-wrap" @click.stop>
          <button
            class="cp__btn cp__btn--ghost cp__btn--selected"
            @click="bulkMenuOpen = !bulkMenuOpen"
          >
            <i class="pi pi-check-square" />
            {{ selected.size }} sélectionné{{ selected.size > 1 ? 's' : '' }}
            <i class="pi pi-chevron-down" style="font-size: 0.6rem" />
          </button>
          <Transition name="dropdown">
            <div v-if="bulkMenuOpen" class="cp__bulk-menu">
              <button class="cp__bulk-opt" @click="bulkExport">
                <i class="pi pi-download" /> {{ t('clients.bulk.exportSel') }}
              </button>
              <button class="cp__bulk-opt" @click="bulkDeactivate">
                <i class="pi pi-ban" /> {{ t('clients.bulk.deactivate') }}
              </button>
              <div class="cp__bulk-sep" />
              <button
                class="cp__bulk-opt cp__bulk-opt--danger"
                @click="bulkDelete"
              >
                <i class="pi pi-trash" /> {{ t('clients.bulk.delete') }}
              </button>
            </div>
          </Transition>
        </div>

        <!-- Vue table / carte -->
        <div class="cp__view-toggle">
          <button
            :class="[
              'cp__view-btn',
              viewMode === 'table' && 'cp__view-btn--active',
            ]"
            @click="viewMode = 'table'"
            title="Vue tableau"
          >
            <i class="pi pi-list" />
          </button>
          <button
            :class="[
              'cp__view-btn',
              viewMode === 'card' && 'cp__view-btn--active',
            ]"
            @click="viewMode = 'card'"
            title="Vue cartes"
          >
            <i class="pi pi-th-large" />
          </button>
        </div>
      </div>
    </div>

    <!-- ── Filtres actifs ────────────────────────────────────────────────── -->
    <div
      v-if="Object.values(colFilters).some((v) => v)"
      class="cp__active-filters"
      @click.stop
    >
      <span class="cp__active-filters-label">{{
        t('clients.activeFilters')
      }}</span>
      <template v-for="(val, col) in colFilters" :key="col">
        <span v-if="val" class="cp__filter-chip">
          <strong>{{ col }}</strong> : {{ val }}
          <button @click="colFilters[col] = ''">
            <i class="pi pi-times" />
          </button>
        </span>
      </template>
      <button
        class="cp__clear-all"
        @click="Object.keys(colFilters).forEach((k) => (colFilters[k] = ''))"
      >
        {{ t('clients.clearAll') }}
      </button>
    </div>

    <!-- ── Skeleton ──────────────────────────────────────────────────────── -->
    <div v-if="isPending" class="cp__skeletons">
      <div v-for="i in 8" :key="i" class="cp__skeleton" />
    </div>

    <!-- ── Erreur ────────────────────────────────────────────────────────── -->
    <div v-else-if="isError" class="cp__empty">
      <i
        class="pi pi-exclamation-circle cp__empty-icon cp__empty-icon--error"
      />
      <p class="cp__empty-title">{{ t('clients.loadError') }}</p>
    </div>

    <!-- ── Aucun résultat ─────────────────────────────────────────────────── -->
    <div v-else-if="filteredOrgs.length === 0" class="cp__empty">
      <i class="pi pi-filter cp__empty-icon" />
      <p class="cp__empty-title">{{ t('clients.noResult') }}</p>
      <button
        class="cp__btn cp__btn--ghost"
        @click="
          search = '';
          Object.keys(colFilters).forEach((k) => (colFilters[k] = ''));
        "
      >
        {{ t('clients.resetFilters') }}
      </button>
    </div>

    <template v-else>
      <!-- ════════ VUE TABLEAU ════════ -->
      <div v-if="viewMode === 'table'" class="cp__table-container">
        <div class="cp__table-scroll">
          <table class="cp__table" @click.stop>
            <thead>
              <tr>
                <!-- Checkbox tout sélectionner -->
                <th class="cp__th cp__th--check">
                  <label class="cp__checkbox">
                    <input
                      type="checkbox"
                      :checked="allSelected"
                      :indeterminate="someSelected && !allSelected"
                      @change="toggleAll"
                    />
                    <span class="cp__checkbox-box" />
                  </label>
                </th>

                <!-- Logo -->
                <th
                  class="cp__th"
                  :style="{
                    width: colWidths.logo + 'px',
                    minWidth: colWidths.logo + 'px',
                  }"
                >
                  <div class="cp__th-inner">
                    <span>{{ t('clients.col.logo') }}</span>
                    <div
                      class="cp__th-resize"
                      @mousedown.stop="startResize('logo', $event)"
                    />
                  </div>
                </th>

                <!-- Nom -->
                <th
                  class="cp__th"
                  :style="{
                    width: colWidths.name + 'px',
                    minWidth: colWidths.name + 'px',
                  }"
                >
                  <div
                    class="cp__th-inner cp__th-inner--sort"
                    @click="setSort('name')"
                  >
                    <span>{{ t('clients.col.name') }}</span>
                    <i
                      class="pi cp__sort-icon"
                      :class="
                        sortKey === 'name'
                          ? sortDir === 'ASC'
                            ? 'pi-sort-up-fill'
                            : 'pi-sort-down-fill'
                          : 'pi-sort'
                      "
                    />
                  </div>
                  <div class="cp__th-filter-wrap" @click.stop>
                    <button
                      class="cp__th-filter-btn"
                      :class="{ 'cp__th-filter-btn--active': colFilters.name }"
                      @click="toggleColFilter('name')"
                    >
                      <i class="pi pi-filter" />
                    </button>
                    <Transition name="dropdown">
                      <div
                        v-if="filterOpenCol === 'name'"
                        class="cp__col-filter-popup"
                      >
                        <input
                          v-model="colFilters.name"
                          class="cp__col-filter-input"
                          :placeholder="t('clients.filterName')"
                          @click.stop
                        />
                      </div>
                    </Transition>
                  </div>
                  <div
                    class="cp__th-resize"
                    @mousedown.stop="startResize('name', $event)"
                  />
                </th>

                <!-- Type -->
                <th
                  class="cp__th"
                  :style="{
                    width: colWidths.type + 'px',
                    minWidth: colWidths.type + 'px',
                  }"
                >
                  <div class="cp__th-inner">
                    <span>{{ t('clients.col.type') }}</span>
                  </div>
                  <div class="cp__th-filter-wrap" @click.stop>
                    <button
                      class="cp__th-filter-btn"
                      :class="{ 'cp__th-filter-btn--active': colFilters.type }"
                      @click="toggleColFilter('type')"
                    >
                      <i class="pi pi-filter" />
                    </button>
                    <Transition name="dropdown">
                      <div
                        v-if="filterOpenCol === 'type'"
                        class="cp__col-filter-popup"
                      >
                        <input
                          v-model="colFilters.type"
                          class="cp__col-filter-input"
                          :placeholder="t('clients.filterType')"
                          @click.stop
                        />
                      </div>
                    </Transition>
                  </div>
                  <div
                    class="cp__th-resize"
                    @mousedown.stop="startResize('type', $event)"
                  />
                </th>

                <!-- TVA -->
                <th
                  class="cp__th"
                  :style="{
                    width: colWidths.tax + 'px',
                    minWidth: colWidths.tax + 'px',
                  }"
                >
                  <div class="cp__th-inner">
                    <span>{{ t('clients.col.tax') }}</span>
                  </div>
                  <div class="cp__th-filter-wrap" @click.stop>
                    <button
                      class="cp__th-filter-btn"
                      :class="{
                        'cp__th-filter-btn--active':
                          colFilters.taxIdentificationNumber,
                      }"
                      @click="toggleColFilter('taxIdentificationNumber')"
                    >
                      <i class="pi pi-filter" />
                    </button>
                    <Transition name="dropdown">
                      <div
                        v-if="filterOpenCol === 'taxIdentificationNumber'"
                        class="cp__col-filter-popup"
                      >
                        <input
                          v-model="colFilters.taxIdentificationNumber"
                          class="cp__col-filter-input"
                          placeholder="BE…"
                          @click.stop
                        />
                      </div>
                    </Transition>
                  </div>
                  <div
                    class="cp__th-resize"
                    @mousedown.stop="startResize('tax', $event)"
                  />
                </th>

                <!-- Commerces -->
                <th
                  class="cp__th"
                  :style="{
                    width: colWidths.stores + 'px',
                    minWidth: colWidths.stores + 'px',
                  }"
                >
                  <div
                    class="cp__th-inner cp__th-inner--sort"
                    @click="setSort('storeCount')"
                  >
                    <span>{{ t('clients.col.stores') }}</span>
                    <i
                      class="pi cp__sort-icon"
                      :class="
                        sortKey === 'storeCount'
                          ? sortDir === 'ASC'
                            ? 'pi-sort-up-fill'
                            : 'pi-sort-down-fill'
                          : 'pi-sort'
                      "
                    />
                  </div>
                  <div class="cp__th-filter-wrap" @click.stop>
                    <button
                      class="cp__th-filter-btn"
                      :class="{
                        'cp__th-filter-btn--active': colFilters.storeCount,
                      }"
                      @click="toggleColFilter('storeCount')"
                    >
                      <i class="pi pi-filter" />
                    </button>
                    <Transition name="dropdown">
                      <div
                        v-if="filterOpenCol === 'storeCount'"
                        class="cp__col-filter-popup"
                      >
                        <input
                          v-model="colFilters.storeCount"
                          type="number"
                          min="0"
                          class="cp__col-filter-input"
                          placeholder="0"
                          @click.stop
                        />
                      </div>
                    </Transition>
                  </div>
                  <div
                    class="cp__th-resize"
                    @mousedown.stop="startResize('stores', $event)"
                  />
                </th>

                <!-- Statut -->
                <th
                  class="cp__th"
                  :style="{
                    width: colWidths.status + 'px',
                    minWidth: colWidths.status + 'px',
                  }"
                >
                  <div
                    class="cp__th-inner cp__th-inner--sort"
                    @click="setSort('activeStatus')"
                  >
                    <span>{{ t('clients.col.status') }}</span>
                    <i
                      class="pi cp__sort-icon"
                      :class="
                        sortKey === 'activeStatus'
                          ? sortDir === 'ASC'
                            ? 'pi-sort-up-fill'
                            : 'pi-sort-down-fill'
                          : 'pi-sort'
                      "
                    />
                  </div>
                  <div class="cp__th-filter-wrap" @click.stop>
                    <button
                      class="cp__th-filter-btn"
                      :class="{
                        'cp__th-filter-btn--active': colFilters.isActive,
                      }"
                      @click="toggleColFilter('isActive')"
                    >
                      <i class="pi pi-filter" />
                    </button>
                    <Transition name="dropdown">
                      <div
                        v-if="filterOpenCol === 'isActive'"
                        class="cp__col-filter-popup"
                      >
                        <button
                          class="cp__col-filter-opt"
                          :class="{ active: colFilters.isActive === 'actif' }"
                          @click="
                            colFilters.isActive = 'actif';
                            filterOpenCol = null;
                          "
                        >
                          {{ t('clients.filterStatus.active') }}
                        </button>
                        <button
                          class="cp__col-filter-opt"
                          :class="{ active: colFilters.isActive === 'inactif' }"
                          @click="
                            colFilters.isActive = 'inactif';
                            filterOpenCol = null;
                          "
                        >
                          {{ t('clients.filterStatus.inactive') }}
                        </button>
                        <button
                          class="cp__col-filter-opt"
                          @click="
                            colFilters.isActive = '';
                            filterOpenCol = null;
                          "
                        >
                          {{ t('clients.filterStatus.all') }}
                        </button>
                      </div>
                    </Transition>
                  </div>
                  <div
                    class="cp__th-resize"
                    @mousedown.stop="startResize('status', $event)"
                  />
                </th>

                <!-- Pays -->
                <th
                  class="cp__th"
                  :style="{
                    width: colWidths.country + 'px',
                    minWidth: colWidths.country + 'px',
                  }"
                >
                  <div class="cp__th-inner">
                    <span>{{ t('clients.col.country') }}</span>
                  </div>
                  <div class="cp__th-filter-wrap" @click.stop>
                    <button
                      class="cp__th-filter-btn"
                      :class="{
                        'cp__th-filter-btn--active': colFilters.countryName,
                      }"
                      @click="toggleColFilter('countryName')"
                    >
                      <i class="pi pi-filter" />
                    </button>
                    <Transition name="dropdown">
                      <div
                        v-if="filterOpenCol === 'countryName'"
                        class="cp__col-filter-popup"
                      >
                        <input
                          v-model="colFilters.countryName"
                          class="cp__col-filter-input"
                          placeholder="Belgium, France…"
                          @click.stop
                        />
                      </div>
                    </Transition>
                  </div>
                  <div
                    class="cp__th-resize"
                    @mousedown.stop="startResize('country', $event)"
                  />
                </th>

                <!-- Actions -->
                <th
                  class="cp__th cp__th--actions"
                  :style="{
                    width: colWidths.actions + 'px',
                    minWidth: colWidths.actions + 'px',
                  }"
                />
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="org in pagedOrgs"
                :key="org.id"
                class="cp__tr"
                :class="{
                  'cp__tr--selected': selected.has(org.id),
                  'cp__tr--inactive': !org.isActive,
                }"
                @click="goToDetail(org.id)"
              >
                <!-- Check -->
                <td class="cp__td cp__td--check">
                  <label class="cp__checkbox" @click.stop>
                    <input
                      type="checkbox"
                      :checked="selected.has(org.id)"
                      @change="toggleRow(org.id)"
                    />
                    <span class="cp__checkbox-box" />
                  </label>
                </td>

                <!-- Logo -->
                <td class="cp__td">
                  <div class="cp__avatar" :style="avatarStyle(org.name)">
                    <img
                      v-if="org.logoUrl"
                      :src="org.logoUrl"
                      :alt="org.name"
                    />
                    <span v-else>{{ initials(org.name) }}</span>
                  </div>
                </td>

                <!-- Nom -->
                <td class="cp__td cp__td--name">
                  <span class="cp__org-name">{{ org.name }}</span>
                  <span v-if="org.description" class="cp__org-desc">{{
                    org.description
                  }}</span>
                </td>

                <!-- Type -->
                <td class="cp__td">
                  <span class="cp__type-chip">{{ typeLabel(org.type) }}</span>
                </td>

                <!-- TVA -->
                <td class="cp__td cp__td--mono">
                  {{ org.taxIdentificationNumber ?? '—' }}
                </td>

                <!-- Commerces -->
                <td class="cp__td cp__td--center">
                  <span
                    class="cp__stores-badge"
                    :class="{ 'cp__stores-badge--zero': !org.storeCount }"
                  >
                    <i class="pi pi-shop" />
                    {{ org.storeCount ?? 0 }}
                  </span>
                </td>

                <!-- Statut -->
                <td class="cp__td">
                  <span
                    class="cp__status"
                    :class="
                      org.isActive
                        ? 'cp__status--active'
                        : 'cp__status--inactive'
                    "
                  >
                    <span class="cp__status-dot" />
                    {{
                      org.isActive
                        ? t('clients.statusBadge.active')
                        : t('clients.statusBadge.inactive')
                    }}
                  </span>
                </td>

                <!-- Pays -->
                <td class="cp__td cp__td--muted">
                  {{
                    (org as any).countryName ?? (org as any).countryCode ?? '—'
                  }}
                </td>

                <!-- Actions -->
                <td class="cp__td cp__td--actions">
                  <button
                    v-if="org.isActive"
                    class="cp__row-action cp__row-action--danger"
                    :title="t('clients.bulk.deactivate')"
                    @click.stop="askDeactivate(org, $event)"
                  >
                    <i class="pi pi-ban" />
                  </button>
                  <button
                    v-else
                    class="cp__row-action cp__row-action--success"
                    :title="t('clients.status.active')"
                    @click.stop="activateOrg(org, $event)"
                  >
                    <i class="pi pi-check-circle" />
                  </button>
                  <button
                    class="cp__row-action"
                    :title="t('clients.card.see')"
                    @click.stop="goToDetail(org.id)"
                  >
                    <i class="pi pi-arrow-right" />
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div class="cp__pagination">
          <span class="cp__pagination-info">
            {{ (page - 1) * PAGE_SIZE + 1 }}–{{
              Math.min(page * PAGE_SIZE, filteredOrgs.length)
            }}
            {{ t('clients.pagination.of') }} {{ filteredOrgs.length }}
          </span>
          <div class="cp__pagination-pages">
            <button class="cp__page-btn" :disabled="page === 1" @click="page--">
              <i class="pi pi-chevron-left" />
            </button>
            <button
              v-for="p in pages"
              :key="p"
              class="cp__page-btn"
              :class="{
                'cp__page-btn--active': p === page,
                'cp__page-btn--ellipsis': p === '…',
              }"
              :disabled="p === '…'"
              @click="typeof p === 'number' && (page = p)"
            >
              {{ p }}
            </button>
            <button
              class="cp__page-btn"
              :disabled="page === totalPages"
              @click="page++"
            >
              <i class="pi pi-chevron-right" />
            </button>
          </div>
          <span class="cp__pagination-info">
            {{ t('clients.pagination.page', { page, total: totalPages }) }}
          </span>
        </div>
      </div>

      <!-- ════════ VUE CARTES ════════ -->
      <div v-else class="cp__cards-wrap">
        <div class="cp__cards" style="gap: 1.5rem">
          <div
            v-for="org in pagedOrgs"
            :key="org.id + '_card'"
            class="cp__card group"
            :class="{ 'cp__card--inactive': !org.isActive }"
            @click="goToDetail(org.id)"
          >
            <!-- Head: avatar + status badge -->
            <div class="cp__card-head">
              <div
                class="cp__card-avatar"
                :class="{ 'cp__card-avatar--active': org.isActive }"
              >
                <img
                  v-if="org.logoUrl"
                  :src="org.logoUrl"
                  :alt="org.name"
                  class="cp__card-avatar-img"
                />
                <span v-else>{{
                  org.name
                    .trim()
                    .split(/\s+/)
                    .slice(0, 2)
                    .map((w: string) => w[0])
                    .join('')
                    .toUpperCase()
                }}</span>
              </div>
              <span
                class="cp__card-status"
                :class="
                  org.isActive
                    ? 'cp__card-status--active'
                    : 'cp__card-status--inactive'
                "
              >
                <span class="cp__card-status-dot" />{{
                  org.isActive
                    ? t('clients.statusBadge.active')
                    : t('clients.statusBadge.inactive')
                }}
              </span>
            </div>

            <!-- Mid: label + name + type -->
            <div class="cp__card-mid">
              <span class="cp__card-entity-label">{{
                t('clients.card.entityLabel')
              }}</span>
              <p class="cp__card-name">{{ org.name }}</p>
              <span class="cp__card-type">{{ typeLabel(org.type) }}</span>
            </div>

            <!-- Info rows -->
            <div class="cp__card-info">
              <div class="cp__card-info-row">
                <span class="cp__card-info-lbl">{{
                  t('clients.card.tva')
                }}</span>
                <span class="cp__card-info-val cp__card-info-val--mono">{{
                  org.taxIdentificationNumber ?? '—'
                }}</span>
              </div>
              <div class="cp__card-info-row">
                <span class="cp__card-info-lbl">{{
                  t('clients.card.stores')
                }}</span>
                <span class="cp__card-info-val">
                  <span
                    class="cp__card-stores"
                    :class="{ 'cp__card-stores--zero': !org.storeCount }"
                  >
                    <i class="pi pi-shop" />{{ org.storeCount ?? 0 }}
                  </span>
                </span>
              </div>
              <div class="cp__card-info-row">
                <span class="cp__card-info-lbl">{{
                  t('clients.card.country')
                }}</span>
                <span class="cp__card-info-val">{{
                  (org as any).countryName ?? (org as any).countryCode ?? '—'
                }}</span>
              </div>
            </div>

            <!-- Hover actions -->
            <div class="cp__card-actions" @click.stop>
              <button
                class="cp__card-btn cp__card-btn--ghost"
                @click.stop="goToDetail(org.id)"
              >
                <i class="pi pi-eye" /> {{ t('clients.card.details') }}
              </button>
              <button
                class="cp__card-btn cp__card-btn--primary"
                @click.stop="goToDetail(org.id)"
              >
                <i class="pi pi-arrow-right" /> {{ t('clients.card.see') }}
              </button>
            </div>
          </div>
        </div>

        <!-- Pagination cartes -->
        <div v-if="totalPages > 1" class="cp__pagination cp__pagination--cards">
          <button class="cp__page-btn" :disabled="page === 1" @click="page--">
            <i class="pi pi-chevron-left" />
          </button>
          <button
            v-for="p in pages"
            :key="p"
            class="cp__page-btn"
            :class="{
              'cp__page-btn--active': p === page,
              'cp__page-btn--ellipsis': p === '…',
            }"
            :disabled="p === '…'"
            @click="typeof p === 'number' && (page = p)"
          >
            {{ p }}
          </button>
          <button
            class="cp__page-btn"
            :disabled="page === totalPages"
            @click="page++"
          >
            <i class="pi pi-chevron-right" />
          </button>
        </div>
      </div>
    </template>

    <!-- ── Modal désactivation (dans le div racine pour éviter fragment) ───── -->
    <Teleport to="body">
      <div
        v-if="deactivateTarget"
        class="cp__modal-backdrop"
        @click.self="deactivateTarget = null"
      >
        <div class="cp__modal">
          <div class="cp__modal-header">
            <span class="cp__modal-title"
              ><i class="pi pi-ban" />
              {{ t('clients.modal.deactivateTitle') }}</span
            >
            <button class="cp__modal-close" @click="deactivateTarget = null">
              <i class="pi pi-times" />
            </button>
          </div>
          <div class="cp__modal-body">
            <p class="cp__modal-text">
              {{
                t('clients.modal.deactivateBody', {
                  name: deactivateTarget.name,
                })
              }}
            </p>
            <p class="cp__modal-text cp__modal-text--muted">
              {{ t('clients.modal.deactivateInfo') }}
            </p>
            <p v-if="deactivateError" class="cp__modal-error">
              <i class="pi pi-exclamation-circle" /> {{ deactivateError }}
            </p>
          </div>
          <div class="cp__modal-footer">
            <button
              class="cp__btn cp__btn--ghost"
              @click="deactivateTarget = null"
            >
              {{ t('clients.modal.cancel') }}
            </button>
            <button
              class="cp__btn cp__btn--danger"
              :disabled="deactivateOrganisation.isPending.value"
              @click="confirmDeactivate"
            >
              <span
                v-if="deactivateOrganisation.isPending.value"
                class="cp__spinner"
              />
              <i v-else class="pi pi-ban" />
              {{
                deactivateOrganisation.isPending.value
                  ? t('clients.modal.confirming')
                  : t('clients.modal.confirm')
              }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
/* ── Layout ────────────────────────────────────────────────────────────── */
.cp {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  min-width: 0;
  padding: 2.5rem 3rem;
  box-sizing: border-box;
}
@media (max-width: 1023px) {
  .cp {
    padding: 1.5rem;
  }
}
@media (max-width: 639px) {
  .cp {
    padding: 1rem;
    gap: 1rem;
  }
}

/* ── Header ────────────────────────────────────────────────────────────── */
.cp__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}
.cp__header-left {
}
.cp__title {
  margin: 0 0 0.25rem;
  font-size: clamp(1.375rem, 3vw, 1.875rem);
  font-weight: 800;
  letter-spacing: -0.025em;
  color: #0b1320;
}
.cp__sub {
  margin: 0;
  font-size: 0.875rem;
  color: var(--nxt-on-surface-muted);
  opacity: 0.65;
}
.cp__header-right {
  display: flex;
  gap: 0.625rem;
  flex-wrap: wrap;
}

/* ── Boutons ────────────────────────────────────────────────────────────── */
.cp__btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  height: 2.375rem;
  padding: 0 1rem;
  border-radius: 0.625rem;
  font-size: 0.8125rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  border: none;
  transition:
    background 0.15s,
    box-shadow 0.15s,
    transform 0.1s;
}
.cp__btn--primary {
  background: #ffd60a;
  color: #0b1320;
  box-shadow: 0 2px 8px rgba(255, 214, 10, 0.35);
}
.cp__btn--primary:hover {
  background: #e6c109;
  box-shadow: 0 6px 16px rgba(255, 214, 10, 0.4);
  transform: translateY(-1px);
}
.cp__btn--ghost {
  background: #fff;
  color: rgba(11, 19, 32, 0.65);
  border: 1px solid rgba(11, 19, 32, 0.12);
}
.cp__btn--ghost:hover {
  background: rgba(11, 19, 32, 0.04);
  color: #0b1320;
  border-color: rgba(11, 19, 32, 0.2);
}
.cp__btn--selected {
  background: rgba(255, 214, 10, 0.12);
  color: #0b1320;
  border-color: rgba(255, 214, 10, 0.4);
}
.cp__btn--sm {
  height: 2rem;
  padding: 0 0.75rem;
  font-size: 0.75rem;
}
.cp__btn .pi {
  font-size: 0.875rem;
}

/* ── Toolbar ─────────────────────────────────────────────────────────────── */
/* ── Tabs statut ─────────────────────────────────────────────────────────── */
.cp__status-tabs {
  display: flex;
  gap: 0.25rem;
  background: rgba(11, 19, 32, 0.05);
  border-radius: 0.75rem;
  padding: 0.25rem;
  flex-shrink: 0;
}
.cp__status-tab {
  height: 2rem;
  padding: 0 0.875rem;
  border-radius: 0.5rem;
  border: none;
  background: transparent;
  color: rgba(11, 19, 32, 0.45);
  font-size: 0.8125rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
}
.cp__status-tab--active {
  background: #0b1320;
  color: #fff;
  box-shadow: 0 1px 4px rgba(11, 19, 32, 0.2);
}
.cp__status-tab:not(.cp__status-tab--active):hover {
  color: #0b1320;
  background: rgba(11, 19, 32, 0.06);
}

.cp__toolbar {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  background: var(--nxt-card-bg, #fff);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.875rem;
  padding: 0.625rem 1rem;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}
.cp__search-wrap {
  flex: 1;
  min-width: 200px;
  position: relative;
  display: flex;
  align-items: center;
}
.cp__search-icon {
  position: absolute;
  left: 0.75rem;
  font-size: 0.875rem;
  color: var(--nxt-on-surface-muted);
  pointer-events: none;
}
.cp__search {
  width: 100%;
  height: 2.25rem;
  padding: 0 2.25rem 0 2.25rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.5rem;
  background: var(--nxt-surface-low);
  font-size: 0.875rem;
  font-family: inherit;
  color: var(--nxt-on-surface);
  outline: none;
  transition: border-color 0.15s;
}
.cp__search:focus {
  border-color: #ffd60a;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(255, 214, 10, 0.2);
}
.cp__search-clear {
  position: absolute;
  right: 0.625rem;
  background: none;
  border: none;
  font-size: 0.75rem;
  color: var(--nxt-on-surface-muted);
  cursor: pointer;
  padding: 0;
}

.cp__toolbar-right {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  flex-shrink: 0;
}
.cp__count {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--nxt-on-surface-muted);
  white-space: nowrap;
}

/* Actions groupées */
.cp__bulk-wrap {
  position: relative;
}
.cp__bulk-menu {
  position: absolute;
  top: calc(100% + 0.375rem);
  right: 0;
  z-index: 50;
  background: var(--nxt-card-bg, #fff);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.75rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  padding: 0.375rem;
  min-width: 13rem;
}
.cp__bulk-opt {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: none;
  border-radius: 0.5rem;
  background: none;
  font-size: 0.8125rem;
  font-weight: 500;
  font-family: inherit;
  color: var(--nxt-on-surface-variant);
  cursor: pointer;
  text-align: left;
  transition: background 0.1s;
}
.cp__bulk-opt:hover {
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface);
}
.cp__bulk-opt--danger {
  color: var(--nxt-error);
}
.cp__bulk-opt--danger:hover {
  background: rgba(186, 26, 26, 0.06);
}
.cp__bulk-opt .pi {
  font-size: 0.875rem;
}
.cp__bulk-sep {
  height: 1px;
  background: var(--nxt-outline-variant);
  margin: 0.25rem 0;
}

/* Vue toggle */
.cp__view-toggle {
  display: flex;
  background: rgba(11, 19, 32, 0.05);
  border-radius: 0.5rem;
  padding: 2px;
  gap: 2px;
}
.cp__view-btn {
  width: 2rem;
  height: 2rem;
  border: none;
  border-radius: 0.375rem;
  background: none;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  color: rgba(11, 19, 32, 0.4);
  cursor: pointer;
  transition:
    background 0.12s,
    color 0.12s;
}
.cp__view-btn--active {
  background: #0b1320;
  color: #fff;
  box-shadow: 0 1px 3px rgba(11, 19, 32, 0.2);
}
.cp__view-btn:not(.cp__view-btn--active):hover {
  color: #0b1320;
  background: rgba(11, 19, 32, 0.07);
}

/* ── Filtres actifs ───────────────────────────────────────────────────────── */
.cp__active-filters {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  padding: 0.5rem 0.875rem;
  background: rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 0.625rem;
  font-size: 0.75rem;
}
.cp__active-filters-label {
  color: var(--nxt-on-surface-muted);
  font-weight: 600;
}
.cp__filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.2rem 0.5rem;
  background: rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 0.375rem;
  color: var(--nxt-on-surface);
}
.cp__filter-chip button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  font-size: 0.625rem;
  color: var(--nxt-on-surface-muted);
}
.cp__clear-all {
  background: none;
  border: none;
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--nxt-primary);
  cursor: pointer;
  margin-left: auto;
}

/* ── Skeleton ──────────────────────────────────────────────────────────── */
.cp__skeletons {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.cp__skeleton {
  height: 3.5rem;
  border-radius: 0.625rem;
  background: linear-gradient(
    90deg,
    var(--nxt-surface-low) 25%,
    var(--nxt-surface-mid) 50%,
    var(--nxt-surface-low) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
}
@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* ── Vide / erreur ───────────────────────────────────────────────────────── */
.cp__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 4rem 1rem;
  text-align: center;
}
.cp__empty-icon {
  font-size: 2.5rem;
  color: var(--nxt-on-surface-muted);
  opacity: 0.2;
}
.cp__empty-icon--error {
  color: var(--nxt-error);
  opacity: 0.6;
}
.cp__empty-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
  opacity: 0.5;
}

/* ── Table ───────────────────────────────────────────────────────────────── */
.cp__table-container {
  background: var(--nxt-card-bg, #fff);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 1rem;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
}
.cp__table-scroll {
  overflow-x: auto;
}
.cp__table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

/* Header */
.cp__th {
  padding: 0;
  background: var(--nxt-surface-low, #f8f9fa);
  border-bottom: 2px solid var(--nxt-outline-variant);
  position: relative;
  user-select: none;
  white-space: nowrap;
  text-align: left;
}
.cp__th--check {
  width: 3rem;
  min-width: 3rem;
  padding: 0 0.75rem;
}
.cp__th--actions {
  width: 3.5rem;
  min-width: 3.5rem;
}

.cp__th-inner {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.75rem 0.875rem;
  font-size: 0.5625rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: var(--nxt-on-surface-muted);
}
.cp__th-inner--sort {
  cursor: pointer;
}
.cp__th-inner--sort:hover {
  color: var(--nxt-on-surface);
}
.cp__sort-icon {
  font-size: 0.625rem;
  opacity: 0.4;
}
.cp__th-inner--sort:hover .cp__sort-icon {
  opacity: 0.8;
}

/* Filter button inside th */
.cp__th-filter-wrap {
  position: absolute;
  right: 1.5rem;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
}
.cp__th-filter-btn {
  width: 1.5rem;
  height: 1.5rem;
  border: none;
  border-radius: 0.3rem;
  background: none;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.625rem;
  color: var(--nxt-on-surface-muted);
  cursor: pointer;
  opacity: 0;
  transition:
    opacity 0.15s,
    background 0.1s;
}
.cp__th:hover .cp__th-filter-btn {
  opacity: 1;
}
.cp__th-filter-btn--active {
  opacity: 1 !important;
  color: var(--nxt-primary) !important;
  background: rgba(0, 0, 0, 0.08);
}
.cp__th-filter-btn:hover {
  background: var(--nxt-surface-mid);
}

.cp__col-filter-popup {
  position: absolute;
  top: calc(100% + 0.25rem);
  right: 0;
  background: var(--nxt-card-bg, #fff);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  padding: 0.375rem;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
  min-width: 10rem;
  z-index: 50;
}
.cp__col-filter-input {
  width: 100%;
  height: 2rem;
  padding: 0 0.625rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.4rem;
  font-size: 0.8125rem;
  font-family: inherit;
  outline: none;
  color: var(--nxt-on-surface);
  background: var(--nxt-surface-low);
  box-sizing: border-box;
}
.cp__col-filter-input:focus {
  border-color: var(--nxt-primary);
  background: var(--nxt-card-bg);
}
.cp__col-filter-opt {
  display: block;
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: none;
  border-radius: 0.4rem;
  background: none;
  text-align: left;
  font-size: 0.8125rem;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  color: var(--nxt-on-surface-variant);
  transition: background 0.1s;
}
.cp__col-filter-opt:hover,
.cp__col-filter-opt.active {
  background: var(--nxt-surface-low);
  color: var(--nxt-primary);
  font-weight: 700;
}

/* Resize handle */
.cp__th-resize {
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  cursor: col-resize;
  z-index: 3;
  background: transparent;
  transition: background 0.1s;
}
.cp__th:hover .cp__th-resize {
  background: rgba(0, 0, 0, 0.3);
}
.cp__th-resize:hover,
.cp__th-resize:active {
  background: var(--nxt-primary);
}

/* Rows */
.cp__tr {
  transition: background 0.12s;
  cursor: pointer;
}
.cp__tr:hover {
  background: var(--nxt-surface-low, #f8f9fa);
}
.cp__tr--selected {
  background: rgba(0, 0, 0, 0.05) !important;
}
.cp__tr--inactive {
  opacity: 0.6;
}

.cp__td {
  padding: 0.875rem;
  border-bottom: 1px solid var(--nxt-outline-variant);
  font-size: 0.875rem;
  color: var(--nxt-on-surface);
  vertical-align: middle;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cp__tr:last-child .cp__td {
  border-bottom: none;
}
.cp__td--check {
  padding: 0.875rem 0.75rem;
}
.cp__td--name {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}
.cp__td--center {
  text-align: center;
}
.cp__td--mono {
  font-family: 'Menlo', 'Consolas', monospace;
  font-size: 0.8125rem;
}
.cp__td--muted {
  color: var(--nxt-on-surface-muted);
  font-size: 0.8125rem;
}
.cp__td--actions {
  text-align: right;
  padding-right: 1rem;
}

/* Checkbox */
.cp__checkbox {
  display: inline-flex;
  align-items: center;
  cursor: pointer;
}
.cp__checkbox input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}
.cp__checkbox-box {
  width: 1.125rem;
  height: 1.125rem;
  border-radius: 0.3rem;
  border: 1.5px solid var(--nxt-outline-variant);
  background: var(--nxt-card-bg);
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    border-color 0.15s,
    background 0.15s;
  flex-shrink: 0;
}
.cp__checkbox input:checked ~ .cp__checkbox-box,
.cp__checkbox input:indeterminate ~ .cp__checkbox-box {
  background: var(--nxt-primary);
  border-color: var(--nxt-primary);
}
.cp__checkbox input:checked ~ .cp__checkbox-box::after {
  content: '';
  display: block;
  width: 0.35rem;
  height: 0.6rem;
  border: 2px solid #fff;
  border-top: none;
  border-left: none;
  transform: rotate(45deg) translateY(-1px);
}
.cp__checkbox input:indeterminate ~ .cp__checkbox-box::after {
  content: '';
  display: block;
  width: 0.5rem;
  height: 1.5px;
  background: #fff;
}

/* Avatar */
.cp__avatar {
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 0.625rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 800;
  overflow: hidden;
  flex-shrink: 0;
}
.cp__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.cp__avatar--lg {
  width: 3rem;
  height: 3rem;
  border-radius: 0.875rem;
  font-size: 1rem;
}

/* Org name */
.cp__org-name {
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.cp__org-desc {
  font-size: 0.75rem;
  color: var(--nxt-on-surface-muted);
  opacity: 0.6;
}

/* Type chip */
.cp__type-chip {
  display: inline-block;
  padding: 0.175rem 0.5rem;
  border-radius: 0.3rem;
  background: var(--nxt-surface-mid);
  color: var(--nxt-on-surface-variant);
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  white-space: nowrap;
}

/* Commerces badge */
.cp__stores-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.8125rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.cp__stores-badge .pi {
  font-size: 0.75rem;
  color: var(--nxt-on-surface-muted);
}
.cp__stores-badge--zero {
  color: var(--nxt-on-surface-muted);
  opacity: 0.4;
}

/* Statut */
.cp__status {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 0.225rem 0.625rem;
  border-radius: 9999px;
  white-space: nowrap;
}
.cp__status--active {
  background: rgba(16, 181, 16, 0.08);
  color: #0a7a0a;
  border: 1px solid rgba(16, 181, 16, 0.2);
}
.cp__status--inactive {
  background: var(--nxt-surface-mid);
  color: var(--nxt-on-surface-muted);
  border: 1px solid var(--nxt-outline-variant);
}
.cp__status-dot {
  width: 0.4rem;
  height: 0.4rem;
  border-radius: 50%;
  background: currentColor;
  flex-shrink: 0;
}

/* Row action */
.cp__row-action {
  width: 2rem;
  height: 2rem;
  border: none;
  border-radius: 50%;
  background: none;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8125rem;
  color: var(--nxt-primary);
  cursor: pointer;
  opacity: 0;
  transition:
    opacity 0.15s,
    background 0.15s;
  margin-left: auto;
}
.cp__tr:hover .cp__row-action {
  opacity: 1;
}
.cp__row-action:hover {
  background: rgba(0, 0, 0, 0.08);
}

/* ── Pagination ──────────────────────────────────────────────────────────── */
.cp__pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.875rem 1.25rem;
  border-top: 1px solid var(--nxt-outline-variant);
  background: var(--nxt-surface-low, #f8f9fa);
  flex-wrap: wrap;
}
.cp__pagination--cards {
  background: none;
  border: none;
  padding: 1rem 0;
  justify-content: center;
}
.cp__pagination-info {
  font-size: 0.75rem;
  color: var(--nxt-on-surface-muted);
}
.cp__pagination-pages {
  display: flex;
  gap: 0.25rem;
}
.cp__page-btn {
  width: 2.25rem;
  height: 2.25rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.5rem;
  background: var(--nxt-card-bg, #fff);
  font-size: 0.8125rem;
  font-weight: 600;
  font-family: inherit;
  color: var(--nxt-on-surface-variant);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    background 0.12s,
    border-color 0.12s,
    color 0.12s;
}
.cp__page-btn:hover:not(:disabled) {
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface);
}
.cp__page-btn--active {
  background: #0b1320;
  border-color: #0b1320;
  color: #fff;
}
.cp__page-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}
.cp__page-btn--ellipsis {
  cursor: default;
  border-color: transparent;
  background: none;
}

/* ── Vue Cartes ─────────────────────────────────────────────────────────── */
.cp__cards-wrap {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background: rgba(240, 243, 255, 0.35);
  border-radius: 2rem;
  padding: 1.75rem;
}
.cp__cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(17rem, 1fr));
}
.cp__card {
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 2rem;
  padding: 1.75rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  position: relative;
  transition:
    transform 0.25s cubic-bezier(0.2, 1, 0.3, 1),
    box-shadow 0.25s cubic-bezier(0.2, 1, 0.3, 1);
  cursor: pointer;
}
.cp__card:hover {
  transform: translateY(-3px);
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.1);
}
.cp__card--inactive {
  opacity: 0.6;
}

/* Card head */
.cp__card-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
}
.cp__card-avatar {
  width: 3rem;
  height: 3rem;
  border-radius: 0.875rem;
  background: #f1f5f9;
  color: #475569;
  border: 1.5px solid rgba(0, 0, 0, 0.06);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9375rem;
  font-weight: 800;
  flex-shrink: 0;
  overflow: hidden;
}
.cp__card-avatar--active {
  background: linear-gradient(135deg, #2d3748, #141c29);
  color: #fff;
  border-color: transparent;
}
.cp__card-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.cp__card-status {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.6875rem;
  font-weight: 700;
  padding: 0.2rem 0.6rem;
  border-radius: 9999px;
  white-space: nowrap;
}
.cp__card-status--active {
  background: rgba(16, 181, 16, 0.08);
  color: #0a7a0a;
  border: 1px solid rgba(16, 181, 16, 0.2);
}
.cp__card-status--inactive {
  background: rgba(0, 0, 0, 0.04);
  color: #94a3b8;
  border: 1px solid rgba(0, 0, 0, 0.08);
}
.cp__card-status-dot {
  width: 0.35rem;
  height: 0.35rem;
  border-radius: 50%;
  background: currentColor;
  flex-shrink: 0;
}

/* Card mid */
.cp__card-mid {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
.cp__card-entity-label {
  font-size: 0.5625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: #94a3b8;
}
.cp__card-name {
  margin: 0;
  font-size: 1rem;
  font-weight: 800;
  color: #141c29;
  line-height: 1.2;
}
.cp__card-type {
  display: inline-block;
  padding: 0.175rem 0.5rem;
  border-radius: 0.3rem;
  background: rgba(0, 0, 0, 0.04);
  color: #64748b;
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-top: 0.125rem;
}

/* Info rows */
.cp__card-info {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.cp__card-info-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}
.cp__card-info-lbl {
  font-size: 0.5625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: #94a3b8;
}
.cp__card-info-val {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #141c29;
}
.cp__card-info-val--mono {
  font-family: 'Menlo', 'Consolas', monospace;
  font-size: 0.75rem;
}

.cp__card-stores {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.8125rem;
  font-weight: 700;
  color: #141c29;
}
.cp__card-stores .pi {
  font-size: 0.75rem;
  color: #94a3b8;
}
.cp__card-stores--zero {
  color: #94a3b8;
}

/* Hover actions */
.cp__card-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.625rem;
  opacity: 0;
  transition: opacity 0.3s;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}
.cp__card:hover .cp__card-actions {
  opacity: 1;
}
.cp__card-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  padding: 0.75rem;
  border-radius: 0.75rem;
  font-size: 0.8125rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  border: none;
  transition:
    background 0.15s,
    color 0.15s;
}
.cp__card-btn--ghost {
  background: #f0f3ff;
  color: #0b1320;
  border: 1px solid rgba(255, 255, 255, 0.6);
}
.cp__card-btn--ghost:hover {
  background: #fff;
}
.cp__card-btn--primary {
  background: #ffd60a;
  color: #0b1320;
  border: none;
}
.cp__card-btn--primary:hover {
  background: #e6c109;
}

/* ── Dropdown transition ──────────────────────────────────────────────────── */
.dropdown-enter-active,
.dropdown-leave-active {
  transition:
    opacity 0.12s,
    transform 0.12s;
}
.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* ── Bouton danger / success ─────────────────────────────────────────────── */
.cp__btn--danger {
  background: var(--nxt-error, #ba1a1a);
  color: #fff;
  border: none;
}
.cp__btn--danger:not(:disabled):hover {
  filter: brightness(1.1);
}
.cp__btn--success {
  background: var(--nxt-status-up, #10b510);
  color: #fff;
  border: none;
}
.cp__btn--success:not(:disabled):hover {
  filter: brightness(1.1);
}

/* ── Row action danger / success ─────────────────────────────────────────── */
.cp__row-action--danger {
  color: var(--nxt-error, #ba1a1a);
}
.cp__row-action--danger:hover {
  background: rgba(186, 26, 26, 0.08);
}
.cp__row-action--success {
  color: var(--nxt-status-up, #10b510);
}
.cp__row-action--success:hover {
  background: rgba(16, 181, 16, 0.08);
}

/* ── Modal désactivation ─────────────────────────────────────────────────── */
.cp__modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}
.cp__modal {
  background: var(--nxt-card-bg);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 1.25rem;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.2);
  width: 100%;
  max-width: 380px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.cp__modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid var(--nxt-outline-variant);
}
.cp__modal-title {
  font-size: 1rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.cp__modal-close {
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  border: 1px solid var(--nxt-outline-variant);
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface-variant);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  transition: all 0.15s;
}
.cp__modal-close:hover {
  background: var(--nxt-surface-mid);
  color: var(--nxt-on-surface);
}
.cp__modal-body {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.cp__modal-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--nxt-outline-variant);
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
}
.cp__modal-text {
  margin: 0;
  font-size: 0.9rem;
  color: var(--nxt-on-surface);
  line-height: 1.5;
}
.cp__modal-text--muted {
  color: var(--nxt-on-surface-muted);
  font-size: 0.8125rem;
}
.cp__modal-error {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-radius: 0.625rem;
  background: var(--nxt-error-container);
  color: var(--nxt-on-error-container);
  font-size: 0.875rem;
  margin: 0;
}
.cp__spinner {
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  display: inline-block;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  animation: cp-spin 0.7s linear infinite;
}
@keyframes cp-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>

<style>
[data-theme='dark'] .cp {
  background: #0c1421;
}
[data-theme='dark'] .cp__title {
  color: #f1f5f9;
}
[data-theme='dark'] .cp__btn--primary {
  background: #ffd60a;
  color: #0b1320;
}
[data-theme='dark'] .cp__btn--ghost {
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.6);
  border-color: rgba(255, 255, 255, 0.1);
}
[data-theme='dark'] .cp__btn--ghost:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}
[data-theme='dark'] .cp__status-tabs {
  background: rgba(255, 255, 255, 0.06);
}
[data-theme='dark'] .cp__status-tab {
  color: rgba(255, 255, 255, 0.35);
}
[data-theme='dark'] .cp__status-tab--active {
  background: #ffd60a;
  color: #0b1320;
}
[data-theme='dark'] .cp__status-tab:not(.cp__status-tab--active):hover {
  color: #fff;
  background: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .cp__view-toggle {
  background: rgba(255, 255, 255, 0.07);
}
[data-theme='dark'] .cp__view-btn {
  color: rgba(255, 255, 255, 0.35);
}
[data-theme='dark'] .cp__view-btn--active {
  background: #ffd60a;
  color: #0b1320;
}
[data-theme='dark'] .cp__view-btn:not(.cp__view-btn--active):hover {
  color: #fff;
  background: rgba(255, 255, 255, 0.1);
}
[data-theme='dark'] .cp__toolbar {
  background: rgba(20, 28, 41, 0.9);
  border-color: rgba(255, 255, 255, 0.07);
}
[data-theme='dark'] .cp__search {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.08);
  color: #f1f5f9;
}
[data-theme='dark'] .cp__search:focus {
  border-color: #ffd60a;
  background: rgba(255, 255, 255, 0.07);
  box-shadow: 0 0 0 3px rgba(255, 214, 10, 0.15);
}
[data-theme='dark'] .cp__page-btn--active {
  background: #ffd60a;
  border-color: #ffd60a;
  color: #0b1320;
}
[data-theme='dark'] .cp__table-container {
  border-color: rgba(255, 255, 255, 0.06);
}
[data-theme='dark'] .cp__th {
  background: var(--nxt-surface-mid, #222527);
  border-color: rgba(255, 255, 255, 0.06);
}
[data-theme='dark'] .cp__td {
  border-color: rgba(255, 255, 255, 0.05);
}
[data-theme='dark'] .cp__tr:hover {
  background: var(--nxt-surface-mid, #222527);
}
[data-theme='dark'] .cp__tr--selected {
  background: rgba(0, 0, 0, 0.07) !important;
}
[data-theme='dark'] .cp__pagination {
  background: var(--nxt-surface-mid, #222527);
  border-color: rgba(255, 255, 255, 0.06);
}
[data-theme='dark'] .cp__page-btn {
  background: var(--nxt-surface-high, #2a2d30);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .cp__col-filter-popup {
  background: var(--nxt-surface-mid, #222527);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .cp__col-filter-input {
  background: var(--nxt-surface-high);
  border-color: rgba(255, 255, 255, 0.08);
  color: var(--nxt-on-surface);
}
[data-theme='dark'] .cp__bulk-menu {
  background: var(--nxt-surface-mid, #222527);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .cp__cards-wrap {
  background: rgba(20, 22, 30, 0.35);
}
[data-theme='dark'] .cp__card {
  background: rgba(30, 32, 38, 0.8);
  border-color: rgba(255, 255, 255, 0.06);
}
[data-theme='dark'] .cp__card-name {
  color: #f1f5f9;
}
[data-theme='dark'] .cp__card-info-val {
  color: #e2e8f0;
}
[data-theme='dark'] .cp__card-avatar {
  background: rgba(255, 255, 255, 0.08);
  color: #94a3b8;
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .cp__card-actions {
  border-top-color: rgba(255, 255, 255, 0.06);
}
[data-theme='dark'] .cp__card-btn--ghost {
  background: rgba(255, 255, 255, 0.06);
  color: #94a3b8;
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .cp__card-btn--ghost:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #f1f5f9;
}
[data-theme='dark'] .cp__card-btn--primary {
  background: #ffd60a;
  color: #0b1320;
  box-shadow: 0 2px 8px rgba(255, 214, 10, 0.25);
}
[data-theme='dark'] .cp__card-btn--primary:hover {
  background: #ffe033;
}
[data-theme='dark'] .cp__checkbox-box {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.15);
}
[data-theme='dark'] .cp__modal {
  background: #111c2e;
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .cp__modal-header {
  border-color: rgba(255, 255, 255, 0.07);
}
[data-theme='dark'] .cp__modal-footer {
  border-color: rgba(255, 255, 255, 0.07);
}
[data-theme='dark'] .cp__modal-title {
  color: #f1f5f9;
}
[data-theme='dark'] .cp__modal-text {
  color: rgba(255, 255, 255, 0.6);
}
[data-theme='dark'] .cp__bulk-menu {
  background: #111c2e;
  border-color: rgba(255, 255, 255, 0.08);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
}
[data-theme='dark'] .cp__bulk-opt {
  color: rgba(255, 255, 255, 0.55);
}
[data-theme='dark'] .cp__bulk-opt:hover {
  background: rgba(255, 255, 255, 0.06);
  color: #fff;
}
[data-theme='dark'] .cp__cards-wrap {
  background: rgba(255, 255, 255, 0.02);
}
[data-theme='dark'] .cp__card {
  background: rgba(20, 28, 41, 0.85);
  border-color: rgba(255, 255, 255, 0.07);
}
[data-theme='dark'] .cp__card:hover {
  border-color: rgba(255, 214, 10, 0.25);
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.4);
}
[data-theme='dark'] .cp__col-filter-popup {
  background: #111c2e;
  border-color: rgba(255, 255, 255, 0.08);
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.5);
}
[data-theme='dark'] .cp__col-filter-input {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: #f1f5f9;
}
[data-theme='dark'] .cp__col-filter-input:focus {
  border-color: #ffd60a;
}
</style>
